// Cliente fetch das functions de auth. Proxy do Vite encaminha /api → :5174.

export interface UserMin {
  id: string;
  email: string;
  name: string;
  /** Quando true, força troca de senha antes de prosseguir (ex: pós forgot-password). */
  mustChangePassword?: boolean;
}

export interface AvailableContext {
  kind: "platform" | "company";
  companyId?: string;
  companyName?: string;
  role: string;
}

export interface UserProfile {
  kind: "platform" | "company";
  role: string;
  companies: Array<{ id: string; name: string }>;
}

export interface MeResponse {
  user: UserMin;
  activeContext: AvailableContext | null;
  contexts: AvailableContext[];
  profiles: UserProfile[];
}

const json = { "content-type": "application/json" };

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init.body ? json : undefined,
    ...init
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body?.error?.code ?? `http_${res.status}`,
      body?.error?.message ?? `Erro ${res.status}`,
      res.status
    );
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

export const api = {
  login: (email: string, password: string) =>
    call<{ user: UserMin; contexts: AvailableContext[]; profiles: UserProfile[] }>(
      "/api/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password })
      }
    ),
  me: () => call<MeResponse>("/api/auth/me"),
  contexts: () => call<{ contexts: AvailableContext[] }>("/api/auth/contexts"),
  /** Switch unificado: passa kind+role e (opcional) companyId. Pra student,
   *  companyId pode ser omitido — backend escolhe a primeira. */
  switchProfile: (input: {
    kind: "platform" | "company";
    role: string;
    companyId?: string;
  }) =>
    call<{ ok: true; activeContext: AvailableContext }>(
      "/api/auth/switch-context",
      {
        method: "POST",
        body: JSON.stringify(input)
      }
    ),
  switchContext: (ctx: AvailableContext) =>
    call<{ ok: true; activeContext: AvailableContext }>("/api/auth/switch-context", {
      method: "POST",
      body: JSON.stringify({
        kind: ctx.kind,
        companyId: ctx.companyId,
        role: ctx.role
      })
    }),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  passkeyLoginOptions: (email: string) =>
    call<{ options: any; challengeId: string }>(
      "/api/auth/passkey/login-options",
      {
        method: "POST",
        body: JSON.stringify({ email })
      }
    ),
  passkeyLoginVerify: (payload: {
    email: string;
    challengeId: string;
    credential: any;
  }) =>
    call<{ user: UserMin; contexts: AvailableContext[]; profiles: UserProfile[] }>(
      "/api/auth/passkey/login-verify",
      {
        method: "POST",
        body: JSON.stringify(payload)
      }
    ),
  signupCheck: (email: string) =>
    call<{ canProceed: boolean }>("/api/auth/signup-check", {
      method: "POST",
      body: JSON.stringify({ email })
    }),
  signupInterest: (payload: {
    email: string;
    fullName: string;
    studyLocation?: string;
    studyArea?: string;
    interestMessage?: string;
  }) =>
    call<{ ok: true; alreadyAllowed?: boolean }>("/api/auth/signup-interest", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  signupComplete: (payload: {
    email: string;
    password: string;
    name: string;
    socialName?: string;
    apelido?: string;
    phone?: string;
    otpToken: string;
    referralCode?: string;
  }) =>
    call<{ user: UserMin; contexts: AvailableContext[]; profiles: UserProfile[] }>(
      "/api/auth/signup-complete",
      {
        method: "POST",
        body: JSON.stringify(payload)
      }
    ),
  signupOtpSend: (email: string) =>
    call<{ ok: true; sentAt: string }>("/api/auth/signup-otp-send", {
      method: "POST",
      body: JSON.stringify({ email })
    }),
  signupOtpVerify: (email: string, code: string) =>
    call<{ ok: true; otpToken: string }>("/api/auth/signup-otp-verify", {
      method: "POST",
      body: JSON.stringify({ email, code })
    }),
  referralValidate: (code: string) =>
    call<{ valid: boolean; indicatorDisplayName?: string }>(
      "/api/auth/referral-validate",
      {
        method: "POST",
        body: JSON.stringify({ code })
      }
    ),
  forgotPassword: (email: string) =>
    call<{ ok: true }>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email })
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    call<{ ok: true }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    })
};
